/* eslint-disable camelcase */
import { AdsConfig } from '../ads/types';

export const LANGUAGE_CODES = [
  'da',
  'fi',
  'fr',
  'en_GB',
  'nb_NO',
  'en_NZ',
  'sv',
] as const;
export type LanguageCode = typeof LANGUAGE_CODES[number];

// These need to be aligned with packages/graphql-schema/src/root.graphql:105
export const MARKET_CODES = [
  'te', // Test country code (see server/react/middlewares/store.js:26)
  'se',
  'gb',
  'fi',
  'fr',
  'no',
  'nz',
  'dk',
] as const;

export type MarketCode = typeof MARKET_CODES[number];

export interface MarketConfig {
  countryCode: MarketCode;
  active: boolean;
  sitename?: string;
  permanentRedirect?: boolean;
  redirectHosts?: string[];
  superSearchEndpoint?: string;
  trackerProxy?: string;
  useClassicUrls?: boolean;
  classicProxy?: {
    host: string;
    port: number;
    secure?: boolean;
  };
  hrefLang?: string[];
  locale: string;
  localeStandard: string;
  sites: {
    production?: string;
    classic?: string;
    analytics?: string;
    brands?: string;
    forum?: string;
    mobile?: string;
  };
  api?: {
    default: string;
  };
  campaigns: {
    base?: string;
    previousCampaignDates?: Record<string, string>;
  };
  googleTagManager?: string;
  translation: LanguageCode;
  timezone?: string;
  currencyCode?: string;
  url: {
    campaigns?: string;
    legacy?: {
      brand?: string;
      category?: string;
      fight?: string;
      forum?: string;
      info?: string;
      lists?: string;
      minhembio?: string;
      plugin?: string;
      product?: string;
      raw?: string;
      store_info?: string;
      user_login?: string;
      mobile_site?: string;
    };
  };
  socialNetworks: SocialNetworks;
  schibstedOtherSitesLinks?: {
    name: string;
    siteUrl: string;
  }[];
  googleSiteVerification?: string;
  campaignFeaturedList?: { campaignId: number; listId: number }[];
  email?: {
    noreply?: string;
  };
  cdn: {
    base?: string;
    origin?: string;
    images?: string;
    offerImages?: string;
  };
  routes?: {
    [K in
      | 'themes'
      | 'campaign'
      | 'productAlert'
      | 'productCompare'
      | 'shopOfTheYear'
      | 'shopOfTheYearCategory'
      | 'shopOfTheYearWinners'
      | 'editorialContentListing'
      | 'categoryIndex'
      | 'expertProductTest'
      | 'expertToplists'
      | 'expertGuide'
      | 'expertProfile'
      | 'expertLandingPage'
      | 'expertCategory'
      | 'changelog']?: {
      path?: string;
      relativePath?: string;
    };
  };
  popularCampaignFilterPaths?: {
    title: string;
    path: string;
    description?: string;
  }[];
  infoPages?: number[];
  infoPageMap?: {
    userAgreement?: number;
    priceInformation?: number;
    privacyPolicy?: number;
    cookiePolicy?: number;
    contact?: number;
    about?: number;
    registerShop?: number;
    media?: number;
    disclaimer?: number;
    aboutReviews?: number;
    adlink?: string;
    themes?: string;
    careers?: string;
    articles?: string;
    shopOfTheYear?: string;
    categoryIndex?: string;
    privacySettings?: string;
    expertLogin?: string;
    experts?: string;
    supportPage?: string;
    newsroom?: string;
    blackFriday?: string;
  };
  featuredArticle?: number;
  newsletter?: {
    apiUrl?: string;
    apiKey?: string;
    list?: string;
    gansubFormId?: string;
  };
  apps?: {
    ios?: string;
    android?: string;
  };
  ads: AdsConfig & {
    active: boolean;
  };
  privacySettings?: {
    [K in 'responsive' | 'desktop' | 'mobile' | 'native']?: {
      cookieVersion?: string;
      cookieUrl?: string;
      privacyUrl?: string;
      privacySettingsUrl?: string;
    };
  };
  modules?: {
    [K in 'categoryInfo' | 'userReviewPurchaseDate' | 'readableProductInfo']?: {
      enabled: boolean;
      datePlaceholderFormat?: string;
    };
  };
  showSchibstedCoBranding?: boolean;
  showClassicSwitch: boolean;
  showBrandingTagline?: boolean;
  trackingToggles?: {
    name?:
      | PrivacySettingToggle.BehaviourTrackingImprovement
      | PrivacySettingToggle.BehaviorTrackingMarketing
      | PrivacySettingToggle.BehaviorTrackingSurveys
      | PrivacySettingToggle.PersonalizedAds
      | PrivacySettingToggle.Personalization;
    default: boolean;
  }[];
  shopOfTheYear: boolean;
  showProductInformationTexts?: boolean;
  offerList?: {
    filters?: {
      hiddenFilters?: FilterType[];
      defaultActiveFilters?: FilterType[];
    };
  };
  paymentProviders?: {
    priorities?: string[];
    groupPriorities?: PaymentProviderGroup[];
  };
  structuredData?: any;
  cdnCache?: {
    enabled: boolean;
    mode: 'internal' | 'external';
  };
  useHashForProductTabs?: boolean;
  lazyLoadIntercom?: boolean;
  productVariants?: {
    shoeSizeStandard: string;
  };
  articles?: {
    aboutExperts?: {
      path: string;
    };
    recruiting?: {
      path: string;
    };
  };
  experts: {
    enabled: boolean;
    apiUrl?: string;
    graphQLUrl?: string;
    clickTrackingEnabled?: boolean;
  };
  keywordLandingPagesEnabled?: boolean;
  featuredCollectionsEnabled?: boolean;
  robotsTxt?: string[];
  fallbackRedirects?: Record<string, string>;
}

interface SocialNetwork {
  link?: string;
}

interface SocialNetworks {
  facebook?: SocialNetwork & {
    enableLogin?: boolean;
    clientID?: string;
    clientSecret?: string;
    callbackURL?: string;
  };
  instagram?: SocialNetwork;
  twitter?: SocialNetwork & {
    enableLogin?: boolean;
    callbackURL?: string;
    consumerKey: string;
    consumerSecret: string;
  };
  schibsted?: SocialNetwork & {
    enableLogin: boolean;
    callbackURL?: string;
    clientID?: string;
    clientSecret?: string;
    host?: string;
  };
}

export type PrivacySettingToggles = { [key in PrivacySettingToggle]: boolean };

export enum PrivacySettingToggle {
  BehaviourTrackingImprovement = 'behavior_tracking_improvement',
  BehaviorTrackingMarketing = 'behavior_tracking_marketing',
  BehaviorTrackingSurveys = 'behavior_tracking_surveys',
  PersonalizedAds = 'personalized_ads',
  Personalization = 'personalization',
}

export enum FilterType {
  PRICES_INC_SHIPPING = 'prices_inc_shipping',
  IN_STOCK = 'in_stock',
  FEATURED_SHOP = 'featured_shop',
  AUTHORIZED_DEALER = 'authorized_dealer',
  PICKUP_ONLY = 'pickup_only',
  INTERNATIONAL = 'international',
  NOT_FOR_SALE = 'not_for_sale',
  VARIANT_SIZE = 'variant_size',
  VARIANT_COLOR = 'variant_color',
  CONDITION = 'condition',
  DELIVERY_METHOD = 'shipping-delivery-method',
  CARRIERS = 'shipping-carriers',
}

export enum PaymentProviderGroup {
  Direct,
  Invoice,
  Card,
}
